import { withErrorBoundary } from 'app/components/error-boundary';
import { OpenSettingsPrompt } from 'app/components/open-settings-prompt';
import { Box, Column, Row } from 'app/design/primitives/box';
import { usePagePadding } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import { useImagePickerCamera } from 'app/hooks/use-imagepicker-camera';
import { useRatingModalNavigationOptions } from 'app/screens/shared/rating-modal-header-options';
import { useAddListingPictures, useUpdateListingPicture, useOneListing } from 'app/network/listing';
import { useRateLoanMutation } from 'app/network/loan';
import { RatingType } from 'app/types/loan';
import type { NavigationScreen } from 'app/types/navigation';
import type { ImagePickerAsset } from 'expo-image-picker';
import { useEffect, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import RNHapticFeedback from 'react-native-haptic-feedback';
import { Alert, KeyboardAvoidingView, Platform, ScrollView, type TextInput } from 'react-native';
import Animated, { FadeIn, FadeInUp, LinearTransition } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useHeaderHeight } from '@react-navigation/elements';
import { log } from 'app/lib/monitoring';
import { createTheme as createDSTheme } from 'app/shopify';
import { SubjectCard } from './update-rating-subject-card';
import { OverallButton } from './update-rating-overall-button';
import { RatingQuestion } from './update-rating-question';
import { FooterActionBar } from './update-rating-footer-action-bar';
import { UpdateRatingCommentStep } from './update-rating-comment-step';
const theme = createDSTheme();

const ORDERED_TYPES: RatingType[] = [
  RatingType.AssetQuality,
  RatingType.LogisticsReliability,
  RatingType.CommunityTrust,
];

type Selections = Partial<Record<RatingType, number>>;
type OverallExperience = 'positive' | 'negative';

const screenStyle = { flex: 1 } as const;
const fillStyle = { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 } as const;

const hapticOptions = {
  enableVibrateFallback: true,
  ignoreAndroidSystemSettings: false,
} as const;

const triggerHaptic = (type: 'impactLight' | 'impactMedium' | 'notificationSuccess') => {
  RNHapticFeedback.trigger(type, hapticOptions);
};

const getOverallRatingValue = (experience: OverallExperience) => (experience === 'positive' ? 5 : 1);
const getExperienceFromOverallRating = (rating?: number): OverallExperience | undefined => {
  if (typeof rating !== 'number') return undefined;
  return rating >= 3 ? 'positive' : 'negative';
};

function getInitialSelections(
  assetQualityRating?: number | null,
  logisticsReliabilityRating?: number | null,
  communityTrustRating?: number | null,
): Selections {
  const selections: Selections = {};
  if (typeof assetQualityRating === 'number') selections[RatingType.AssetQuality] = assetQualityRating;
  if (typeof logisticsReliabilityRating === 'number') {
    selections[RatingType.LogisticsReliability] = logisticsReliabilityRating;
  }
  if (typeof communityTrustRating === 'number') selections[RatingType.CommunityTrust] = communityTrustRating;
  return selections;
}

function getOverallPrompt(t: ReturnType<typeof useTranslation>['t'], isLender: boolean) {
  return isLender ? t('newRating.figmaOverallQuestion.lender') : t('newRating.figmaOverallQuestion.borrower');
}

function RatingQuestionsStep({
  isLender,
  onOverallSelect,
  onSelect,
  overallExperience,
  questionOffsets,
  selections,
  subjectImage,
  subjectName,
  subjectSubtitle,
  visibleCount,
}: {
  isLender: boolean;
  onOverallSelect: (experience: OverallExperience) => void;
  onSelect: (type: RatingType, index: number, value: number) => void;
  overallExperience?: OverallExperience;
  questionOffsets: RefObject<Partial<Record<RatingType, number>>>;
  selections: Selections;
  subjectImage?: string | null;
  subjectName: string;
  subjectSubtitle: string;
  visibleCount: number;
}) {
  const { t } = useTranslation('ratings');

  return (
    <>
      <Animated.View entering={FadeInUp.duration(220)}>
        <Column gap="spacing.general.x4">
          <SubjectCard
            image={subjectImage}
            name={subjectName}
            recommended={overallExperience === 'positive'}
            subtitle={subjectSubtitle}
          />

          <Column gap="spacing.general.x4">
            <Text color="text.primary" fontSize={24} fontWeight="700" lineHeight={33.6}>
              {getOverallPrompt(t, isLender)}
            </Text>

            <Row gap="spacing.general.x2" width="100%">
              <OverallButton
                emoji="👍"
                onPress={() => onOverallSelect('positive')}
                selected={overallExperience === 'positive'}
                tone="positive"
              />
              <OverallButton
                emoji="👎"
                onPress={() => onOverallSelect('negative')}
                selected={overallExperience === 'negative'}
                tone="negative"
              />
            </Row>

            {visibleCount > 0 ? (
              <Text color="text.primary" fontSize={15} fontWeight="600" marginBottom="spacing.general.x2">
                {t('newRating.figmaDetailSectionTitle')}
              </Text>
            ) : null}
          </Column>
        </Column>
      </Animated.View>

      <Column gap="spacing.general.x4">
        {ORDERED_TYPES.slice(0, visibleCount).map((type, index) => (
          <Animated.View
            entering={index === 0 ? FadeIn.duration(180) : FadeInUp.duration(260)}
            key={type}
            layout={LinearTransition.duration(220)}
            onLayout={(event) => {
              questionOffsets.current[type] = event.nativeEvent.layout.y;
            }}
          >
            <RatingQuestion
              index={index}
              isLender={isLender}
              onChange={(value) => onSelect(type, index, value)}
              type={type}
              value={selections[type]}
            />
          </Animated.View>
        ))}
      </Column>
    </>
  );
}

const UpdateRatingScreenComponent: NavigationScreen<'update-rating'> = ({ navigation, route }) => {
  const {
    loanId,
    channelUrl,
    isLender,
    subjectImage,
    subjectName,
    initialExperience,
    overallRating,
    assetQualityRating,
    logisticsReliabilityRating,
    communityTrustRating,
    comment: initialComment,
    listingId,
  } = route.params;
  const { t } = useTranslation('ratings');
  const { t: tOneChannel } = useTranslation('one-channel');
  const insets = useSafeAreaInsets();
  const headerHeight = useHeaderHeight();
  const pagePadding = usePagePadding(true);
  const scrollRef = useRef<ScrollView>(null);
  const commentInputRef = useRef<TextInput>(null);
  const questionOffsets = useRef<Partial<Record<RatingType, number>>>({});
  const commentOffset = useRef(0);

  const initialSelections = getInitialSelections(assetQualityRating, logisticsReliabilityRating, communityTrustRating);
  const derivedInitialExperience = initialExperience || getExperienceFromOverallRating(overallRating);

  const [selections, setSelections] = useState<Selections>(initialSelections);
  const [overallExperience, setOverallExperience] = useState<OverallExperience | undefined>(derivedInitialExperience);
  const [comment, setComment] = useState(initialComment ?? '');
  const [commentImage, setCommentImage] = useState<ImagePickerAsset | null>(null);
  const [visibleCount, setVisibleCount] = useState(
    Object.values(initialSelections).some((value) => typeof value === 'number')
      ? ORDERED_TYPES.length
      : derivedInitialExperience
        ? 1
        : 0,
  );
  const [isCommentStep, setIsCommentStep] = useState(false);

  // Fetch listing to check for existing pictures
  const { data: listing } = useOneListing(listingId, 'update-rating');

  // Find the most recent picture (likely from previous rating edit)
  // Heuristic: pictures array is sorted by position, take the last one if it exists
  const existingPictureId = listing?.pictures?.[listing.pictures.length - 1]?.id;

  const { mutate: addPictures } = useAddListingPictures(listingId, undefined, {
    onError: (error) => {
      log('Failed to upload rating picture:', error);
    },
  });

  const { mutate: updatePicture } = useUpdateListingPicture(listingId, existingPictureId ?? '', undefined, {
    onError: (error) => {
      log('Failed to update rating picture:', error);
    },
  });

  const { onUseImagePickerCamera, settingsPromptState } = useImagePickerCamera(
    (result) => {
      setCommentImage(result.assets[0] ?? null);
    },
    undefined,
    {
      allowsMultipleSelection: false,
      selectionLimit: 1,
      orderedSelection: false,
      aspect: [1, 1],
    },
  );

  useRatingModalNavigationOptions(navigation, { isCommentStep, setIsCommentStep, t });

  const { mutate: rateLoan, isPending } = useRateLoanMutation(
    { channel_url: channelUrl },
    {
      onSuccess: (_result, submittedRating) => {
        triggerHaptic('notificationSuccess');

        // Upload or update picture only after rating submission succeeds (non-blocking)
        const pendingImage = commentImage;
        if (submittedRating.rating === getOverallRatingValue('positive') && pendingImage) {
          if (existingPictureId) {
            updatePicture(pendingImage);
          } else {
            addPictures([pendingImage]);
          }
        }

        const submittedRatingsByType = new Map(
          submittedRating.ratings?.map((rating) => [rating.type, rating.rating] as const) ?? [],
        );
        const submittedExperience =
          submittedRating.rating === getOverallRatingValue('positive') ? 'positive' : 'negative';

        navigation.replace('rating-thanks', {
          loanId,
          listingId,
          channelUrl,
          isLender,
          subjectImage,
          subjectName,
          recommended: submittedExperience === 'positive',
          initialExperience: submittedExperience,
          overallRating: submittedRating.rating,
          assetQualityRating: submittedRatingsByType.get(RatingType.AssetQuality) ?? null,
          logisticsReliabilityRating: submittedRatingsByType.get(RatingType.LogisticsReliability) ?? null,
          communityTrustRating: submittedRatingsByType.get(RatingType.CommunityTrust) ?? null,
          comment: submittedRating.comment ?? null,
        });
      },
      onError: () => {
        Alert.alert(t('newRating.errorTitle'), t('newRating.errorMessage'));
      },
    },
  );

  const answeredRatings = ORDERED_TYPES.flatMap((type) => {
    const rating = selections[type];
    return typeof rating === 'number' ? [{ type, rating }] : [];
  });

  useEffect(() => {
    if (!isCommentStep) return;

    const timeout = setTimeout(() => {
      scrollRef.current?.scrollTo({
        y: Math.max(0, commentOffset.current - theme.spacing['spacing.general.x4']),
        animated: true,
      });
      commentInputRef.current?.focus();
    }, 220);

    return () => clearTimeout(timeout);
  }, [isCommentStep]);

  const handleOverallSelect = (experience: OverallExperience) => {
    if (isPending) return;

    triggerHaptic('impactLight');
    setOverallExperience(experience);

    if (visibleCount > 0) return;

    setVisibleCount(1);
    setTimeout(() => {
      const firstType = ORDERED_TYPES[0];
      scrollRef.current?.scrollTo({
        y: Math.max(
          0,
          (firstType ? (questionOffsets.current[firstType] ?? 0) : 0) - theme.spacing['spacing.general.x12'],
        ),
        animated: true,
      });
    }, 180);
  };

  const handleSubmit = () => {
    if (!overallExperience || isPending) return;

    if (isCommentStep && overallExperience === 'negative' && !comment.trim()) return;
    if (!isCommentStep && answeredRatings.length < ORDERED_TYPES.length) return;

    if (!isCommentStep) {
      triggerHaptic('impactMedium');
      setIsCommentStep(true);
      return;
    }

    triggerHaptic('impactMedium');
    rateLoan({
      id: loanId,
      rating: getOverallRatingValue(overallExperience),
      ratings: answeredRatings,
      comment: comment.trim() || undefined,
    });
  };

  const handleSelect = (type: RatingType, index: number, value: number) => {
    if (isPending) return;

    const wasAnswered = typeof selections[type] === 'number';

    triggerHaptic('impactLight');
    setSelections((prev) => ({ ...prev, [type]: value }));

    if (!wasAnswered && index === visibleCount - 1 && index < ORDERED_TYPES.length - 1) {
      const nextType = ORDERED_TYPES[index + 1];
      if (!nextType) return;

      setVisibleCount((current) => Math.max(current, index + 2));

      setTimeout(() => {
        scrollRef.current?.scrollTo({
          y: Math.max(0, (questionOffsets.current[nextType] ?? 0) - theme.spacing['spacing.general.x4']),
          animated: true,
        });
      }, 180);
    }
  };

  const isCommentRequired = overallExperience === 'negative';
  const areAllQuestionsAnswered = answeredRatings.length === ORDERED_TYPES.length;
  const footerDisabled =
    !overallExperience ||
    isPending ||
    (!isCommentStep && !areAllQuestionsAnswered) ||
    (isCommentStep && isCommentRequired && !comment.trim());
  const isLenderRole = isLender;
  const subjectSubtitle = isLenderRole ? t('newRating.figmaSubjectBorrowerLabel') : tOneChannel('banner.memberOffer');

  return (
    <Box flex={1}>
      <Box pointerEvents="none" style={fillStyle}>
        <Box backgroundColor="surface.default" flex={1} />
      </Box>

      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={headerHeight + theme.spacing['spacing.general.x2']}
        style={screenStyle}
      >
        <ScrollView
          contentContainerStyle={[
            pagePadding,
            { paddingBottom: theme.spacing['spacing.general.x12'] + insets.bottom + 120 },
          ]}
          keyboardShouldPersistTaps="handled"
          ref={scrollRef}
          showsVerticalScrollIndicator={false}
        >
          {isCommentStep ? (
            <UpdateRatingCommentStep
              comment={comment}
              commentImage={commentImage}
              commentInputRef={commentInputRef}
              commentOffset={commentOffset}
              isCommentRequired={isCommentRequired}
              onChangeComment={setComment}
              onUseImagePickerCamera={onUseImagePickerCamera}
              overallExperience={overallExperience}
              subjectImage={subjectImage}
              subjectName={subjectName}
              subjectSubtitle={subjectSubtitle}
            />
          ) : (
            <RatingQuestionsStep
              isLender={isLender}
              onOverallSelect={handleOverallSelect}
              onSelect={handleSelect}
              overallExperience={overallExperience}
              questionOffsets={questionOffsets}
              selections={selections}
              subjectImage={subjectImage}
              subjectName={subjectName}
              subjectSubtitle={subjectSubtitle}
              visibleCount={visibleCount}
            />
          )}
        </ScrollView>

        <FooterActionBar
          disabled={footerDisabled}
          isCommentStep={isCommentStep}
          isLoading={isPending}
          onPress={handleSubmit}
        />
      </KeyboardAvoidingView>

      <OpenSettingsPrompt
        isOpen={settingsPromptState.isOpen}
        onClose={settingsPromptState.onClose}
        settingName="camera"
      />
    </Box>
  );
};

export const UpdateRatingScreen: NavigationScreen<'update-rating'> = withErrorBoundary(UpdateRatingScreenComponent);
